import React, { useEffect, useState } from "react";
import {
  Spin,
  Button,
  Modal,
  Input,
  Typography,
  Tag,
  message,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { getApi, postApi, deleteApi } from "../../redux/rest/restSlice";
import {
  LIST_ROLES,
  LIST_PERMISSIONS,
  CREATE_ROLE,
  DELETE_ROLE,
} from "../../constants/actionTypes";
import { unwrapResult } from "@reduxjs/toolkit";
import { useDispatch } from "react-redux";
import { rolesTables } from "../../constants/rolesTables";
import DataTable from "../DataTable";

const { Text } = Typography;

const RolesOverviewTable = () => {
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [roleName, setRoleName] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  const dispatch = useDispatch();

  /* ---------------- Fetch Roles ---------------- */
  const fetchRolesList = () => {
    setLoading(true);
    dispatch(getApi({ endpoint: LIST_ROLES }))
      .then(unwrapResult)
      .then((result) => {
        setRoles(result.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  /* -------------- Fetch Permissions ------------ */
  const fetchPermissionsList = () => {
    dispatch(getApi({ endpoint: LIST_PERMISSIONS }))
      .then(unwrapResult)
      .then((result) => {
        setPermissions(result.data);
      });
  };

  useEffect(() => {
    fetchRolesList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------------- Modal Actions -------------- */
  const openModal = () => {
    setModalOpen(true);
    setRoleName("");
    setSelectedPermissions([]);
    fetchPermissionsList();
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const togglePermission = (id) => {
    setSelectedPermissions((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id],
    );
  };

  const handleCreateRole = () => {
    if (!roleName.trim()) {
      return message.error("Role name is required");
    }

    if (!selectedPermissions.length) {
      return message.error("Select at least one permission");
    }

    setLoading(true);

    dispatch(
      postApi({
        endpoint: CREATE_ROLE,
        payload: {
          name: roleName,
          permission_ids: selectedPermissions,
        },
      }),
    )
      .then(unwrapResult)
      .then(() => {
        message.success("Role created successfully");
        closeModal();
        fetchRolesList();
      })
      .finally(() => setLoading(false));
  };

  const handleDeleteRole = (id) => {
    setLoading(true);

    dispatch(
      deleteApi({
        endpoint: `${DELETE_ROLE}/${id}`,
      }),
    )
      .then(unwrapResult)
      .then(() => {
        message.success("Role deleted successfully");
        fetchRolesList();
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const firstLoad = loading && !(roles && roles.length);

  return (
    <Spin spinning={loading && !firstLoad}>
      {/* 🔹 Header */}
      <div className="table-header table-header--end">
        <Button type="primary" icon={<PlusOutlined />} onClick={openModal}>
          Add Role
        </Button>
      </div>

      {/* 🔹 Table */}
      <DataTable
        skeleton={firstLoad}
        scrollX={600}
        rowKey="id"
        className="mt-16"
        dataSource={roles}
        pagination={false}
        emptyProps={{ noun: "roles" }}
        columns={[
          ...rolesTables(),
          {
            title: "Actions",
            key: "actions",
            align: "right",
            className: "col-actions",
            fixed: "right",
            render: (_, record) => (
              ![1, 2, 3].includes(record.id) && (<Button
                danger
                type="primary"
                icon={<DeleteOutlined />}
                onClick={() =>
                  Modal.confirm({
                    title: "Are you sure you want to delete this role?",
                    content: "This action cannot be undone.",
                    okText: "Delete",
                    okType: "danger",
                    cancelText: "Cancel",
                    onOk: () => handleDeleteRole(record.id),
                  })
                }
              >
                Delete
              </Button>)
            ),
          },
        ]}
      />

      {/* 🔹 Create Role Modal */}
      <Modal
        title="Create Role"
        open={modalOpen}
        onCancel={closeModal}
        onOk={handleCreateRole}
        okText="Create Role"
        width={700}
      >
        {/* Role Name */}
        <div className="field-block">
          <Text className="field-label">Role Name</Text>
          <Input
            placeholder="Enter role name"
            value={roleName}
            onChange={(e) => setRoleName(e.target.value)}
          />
        </div>

        {/* Permissions */}
        <div className="field-block">
          <Text className="field-label">Permissions</Text>
          <Text className="field-hint">
            Select the permissions this role should grant
          </Text>

          <div className="permissions-tags">
          {permissions.map((p) => {
            const selected = selectedPermissions.includes(p.id);
            return (
              <Tag
                key={p.id}
                color={selected ? "blue" : "default"}
                className="permission-tag"
                onClick={() => togglePermission(p.id)}
              >
                {p.permission}
              </Tag>
            );
          })}
          </div>
        </div>
      </Modal>
    </Spin>
  );
};

export default RolesOverviewTable;
